import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, CheckCircle2, ChefHat, PackageCheck, PartyPopper, XCircle, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import { BotanicalDivider, BotanicalSpray } from "../components/Botanical";

const ease = [0.22, 1, 0.36, 1];

const STEPS = [
  { key: "Pending", label: "Request received", icon: Clock, text: "Lall has your request and will review it personally within 24 hours." },
  { key: "Accepted", label: "Accepted", icon: CheckCircle2, text: "Your order is confirmed — design details and payment are being finalised with you." },
  { key: "In Preparation", label: "In preparation", icon: ChefHat, text: "Your bake is being made by hand in the studio." },
  { key: "Ready", label: "Ready", icon: PackageCheck, text: "Finished and looking beautiful — collection or delivery is being arranged." },
  { key: "Completed", label: "Completed", icon: PartyPopper, text: "Enjoy every bite. Thank you for celebrating with Velvet Bakes." },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const TrackOrder = () => {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/orders/track/${token}`)
      .then((res) => setOrder(res.data))
      .catch(() => setError("We couldn't find an order for this link — please check it was copied in full."));
  }, [token]);

  const currentIndex = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <div className="min-h-screen bg-[#F9F6F0] relative overflow-hidden" data-testid="track-page">
      <div className="absolute -top-8 -left-10 w-64 text-[#C5A059]/30 pointer-events-none">
        <BotanicalSpray flip />
      </div>
      <div className="absolute bottom-0 right-0 w-72 text-[#B87D8E]/25 pointer-events-none rotate-180">
        <BotanicalSpray />
      </div>

      <header className="relative z-10 max-w-3xl mx-auto px-6 pt-10 flex items-center justify-between">
        <Link to="/" data-testid="track-home-link" aria-label="Back to Velvet Bakes home">
          <Logo />
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#8B5D6C] hover:text-[#4A2834] transition-colors" data-testid="track-back-link">
          <ArrowLeft size={15} /> Back to the site
        </Link>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-14 pb-24">
        {!order && !error && (
          <p className="text-center text-[#987885] font-serif text-2xl animate-pulse" data-testid="track-loading">Fetching your order…</p>
        )}

        {error && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }} className="text-center" data-testid="track-error">
            <p className="font-serif text-4xl text-[#4A2834]">Hmm, that link doesn't match an order</p>
            <p className="mt-4 text-[#70505D]">{error}</p>
            <p className="mt-2 text-sm text-[#987885]">If it keeps happening, message Lall from the chat on the homepage.</p>
          </motion.div>
        )}

        {order && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#8B5D6C] mb-4">Order Tracking</p>
              <h1 className="font-serif text-4xl md:text-5xl text-[#4A2834]" data-testid="track-greeting">
                Hello, {order.name}
              </h1>
              <BotanicalDivider className="w-56 mx-auto mt-6 text-[#C5A059]" />
            </div>

            {order.status === "Cancelled" ? (
              <div className="rounded-3xl border border-red-300/60 bg-red-50/60 p-10 text-center" data-testid="track-cancelled">
                <XCircle size={36} className="mx-auto text-red-400" />
                <p className="font-serif text-3xl text-[#4A2834] mt-4">This order has been cancelled</p>
                <p className="mt-3 text-sm text-[#70505D]">If this doesn't look right, please message Lall from the homepage chat.</p>
              </div>
            ) : (
              <div className="bg-white/70 border border-[#C5A059]/30 rounded-3xl p-8 md:p-12 shadow-xl shadow-[#8B5D6C]/10" data-testid="track-timeline">
                {STEPS.map((step, i) => {
                  const done = i < currentIndex;
                  const current = i === currentIndex;
                  return (
                    <motion.div
                      key={step.key}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.15 + i * 0.12, ease }}
                      className="flex gap-5 relative"
                      data-testid={`track-step-${step.key.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {i < STEPS.length - 1 && (
                        <span className={`absolute left-[23px] top-12 bottom-0 w-px ${done ? "bg-[#C5A059]" : "bg-[#8B5D6C]/15"}`} />
                      )}
                      <div
                        className={`shrink-0 w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${
                          current
                            ? "bg-[#8B5D6C] border-[#C5A059] text-[#F9F6F0]"
                            : done
                              ? "border-[#C5A059] text-[#C5A059]"
                              : "border-[#8B5D6C]/20 text-[#987885]/50"
                        }`}
                      >
                        <step.icon size={19} />
                      </div>
                      <div className="pb-10">
                        <p className={`font-serif text-2xl leading-tight ${current || done ? "text-[#4A2834]" : "text-[#987885]/60"}`}>
                          {step.label}
                          {current && (
                            <span className="ml-3 align-middle text-[10px] font-sans uppercase tracking-[0.2em] font-semibold text-[#F9F6F0] bg-[#C5A059] rounded-full px-3 py-1" data-testid="track-current-badge">
                              You are here
                            </span>
                          )}
                        </p>
                        <p className={`text-sm mt-1.5 leading-relaxed ${current || done ? "text-[#70505D]" : "text-[#987885]/50"}`}>{step.text}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5, ease }}
              className="mt-8 bg-[#F3EEE6]/80 border border-[#C5A059]/25 rounded-3xl p-8"
              data-testid="track-order-summary"
            >
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8B5D6C] mb-5">Your order</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-[#70505D]">
                <p><span className="text-[#987885]">Occasion:</span> {order.occasion}</p>
                <p><span className="text-[#987885]">Date needed:</span> {fmtDate(order.event_date)}</p>
                <p><span className="text-[#987885]">Cake:</span> {order.servings}</p>
                <p><span className="text-[#987885]">Flavour:</span> {order.flavour}</p>
                {order.extras?.length > 0 && <p className="sm:col-span-2"><span className="text-[#987885]">Finishing touches:</span> {order.extras.join(", ")}</p>}
                {order.estimate && <p className="sm:col-span-2"><span className="text-[#987885]">Estimate:</span> <span className="font-serif text-lg text-[#8B5D6C]">{order.estimate}</span></p>}
              </div>
              <p className="mt-6 pt-5 border-t border-[#8B5D6C]/10 text-xs text-[#987885] leading-relaxed">
                Keep this link — it updates automatically as your order moves along. Questions?
                Message Lall from the chat bubble on the homepage.
              </p>
            </motion.div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default TrackOrder;
